/**
 * Cross-tab token coordination utilities.
 *
 * 1. getTokensFromStorage — reads tokens from localStorage so the axios
 *    interceptor can detect when another tab already refreshed the JWT.
 *
 * 2. acquireRefreshLock / releaseRefreshLock — BroadcastChannel-based lock
 *    ensuring only one tab calls /token/refresh/ at a time. Other tabs wait
 *    for the winner to finish, then read the updated tokens from localStorage.
 *    Falls back to a localStorage-based lock when BroadcastChannel is
 *    unavailable (e.g. older browsers, web workers).
 */

const LOCK_TIMEOUT_MS = 10_000;
const LOCK_KEY = "profit:token_refresh_lock";
const CHANNEL_NAME = "profit:token_refresh";

export function getTokensFromStorage() {
  try {
    const persisted = localStorage.getItem("persist:root");
    if (!persisted) return null;
    const state = JSON.parse(persisted);
    if (!state.auth) return null;
    const auth = JSON.parse(state.auth);
    return auth.authTokens || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// BroadcastChannel lock (primary) with localStorage fallback
// ---------------------------------------------------------------------------

let channel = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
} catch {
  // BroadcastChannel not available — fall back to localStorage lock
}

/**
 * Try to acquire the cross-tab refresh lock.
 *
 * Returns a Promise that resolves to:
 *   { acquired: true }  — this tab should perform the refresh
 *   { acquired: false } — another tab refreshed; read tokens from storage
 *
 * If the lock holder crashes, a 10-second timeout releases it automatically.
 */
export function acquireRefreshLock() {
  if (channel) {
    return acquireViaBroadcastChannel();
  }
  return acquireViaLocalStorage();
}

/**
 * Release the lock and notify waiting tabs.
 * Call this in the .finally() of the refresh request.
 */
export function releaseRefreshLock() {
  if (channel) {
    try {
      channel.postMessage({ type: "refresh_done" });
    } catch {
      // Channel may have been closed
    }
  }
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {
    // Best-effort cleanup
  }
}

// ---------------------------------------------------------------------------
// BroadcastChannel strategy
// ---------------------------------------------------------------------------

function acquireViaBroadcastChannel() {
  const now = Date.now();
  const existing = readLock();

  if (existing && now - existing.timestamp < LOCK_TIMEOUT_MS) {
    return waitForBroadcastRelease();
  }

  writeLock(now);
  return Promise.resolve({ acquired: true });
}

function waitForBroadcastRelease() {
  return new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      channel.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };

    const onMessage = (e) => {
      if (e.data?.type === "refresh_done") {
        cleanup();
        resolve({ acquired: false });
      }
    };

    channel.addEventListener("message", onMessage);

    const timer = setTimeout(() => {
      cleanup();
      resolve({ acquired: true });
    }, LOCK_TIMEOUT_MS);
  });
}

// ---------------------------------------------------------------------------
// localStorage fallback strategy
// ---------------------------------------------------------------------------

function acquireViaLocalStorage() {
  const now = Date.now();
  const existing = readLock();

  if (existing && now - existing.timestamp < LOCK_TIMEOUT_MS) {
    return waitForStorageRelease();
  }

  writeLock(now);
  return Promise.resolve({ acquired: true });
}

function waitForStorageRelease() {
  return new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("storage", onStorage);
      clearTimeout(timer);
    };

    const onStorage = (e) => {
      if (e.key === LOCK_KEY && !e.newValue) {
        cleanup();
        resolve({ acquired: false });
      }
    };

    window.addEventListener("storage", onStorage);

    const timer = setTimeout(() => {
      cleanup();
      resolve({ acquired: true });
    }, LOCK_TIMEOUT_MS);
  });
}

// ---------------------------------------------------------------------------
// Shared lock helpers
// ---------------------------------------------------------------------------

function readLock() {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLock(timestamp) {
  try {
    localStorage.setItem(LOCK_KEY, JSON.stringify({ timestamp }));
  } catch {
    // Storage full or unavailable — proceed anyway
  }
}
